
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useActivityContext } from "@/hooks/useActivityContext";
import TestInterface from "@/components/tests/TestInterface";
import TestModeSelection from "@/components/tests/TestModeSelection";
import TopicSelectionDialog, { Topic } from "@/components/tests/TopicSelectionDialog";
import Seo from "@/components/seo/Seo";

type TestMode = "exam" | "practice";

const TestPage = () => {
  const { grade, subject, category } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { updateActivityContext } = useActivityContext();
  
  const [testStarted, setTestStarted] = useState(false);
  const [testMode, setTestMode] = useState<TestMode | null>(null);
  const [subjectData, setSubjectData] = useState<any>(null);
  const [categoryData, setCategoryData] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [showTopicDialog, setShowTopicDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModeSelection, setShowModeSelection] = useState(true);
  const [timeElapsed, setTimeElapsed] = useState(0);

  useEffect(() => {
    fetchSubjectAndCategories();
  }, [grade, subject, category]);

  useEffect(() => {
    // Update activity context when test starts
    if (testStarted && questions.length > 0) {
      updateActivityContext({
        currentActivity: 'test',
        progress: 0,
        isActive: true
      });
    }
  }, [testStarted, questions, subjectData, categoryData, testMode, updateActivityContext]);

  const fetchSubjectAndCategories = async () => {
    try {
      const { data: subjectInfo, error: subjectError } = await supabase
        .from('subjects')
        .select('*')
        .eq('grade', grade)
        .ilike('name', `%${subject?.replace('-', ' ')}%`)
        .single();

      if (subjectError) {
        console.error('Error fetching subject:', subjectError);
        toast({
          title: "Error",
          description: "Failed to load subject information.",
          variant: "destructive",
        });
        return;
      }

      setSubjectData(subjectInfo);

      const { data: categoriesData, error: categoriesError } = await supabase
        .from('subjects_categories')
        .select('*')
        .eq('subject_id', subjectInfo.id)
        .order('name');

      if (categoriesError) {
        console.error('Error fetching categories:', categoriesError);
      } else {
        setCategories(categoriesData || []);
      }

      if (category && subjectInfo) {
        const { data: categoryInfo, error: categoryError } = await supabase
          .from('subjects_categories')
          .select('*')
          .eq('subject_id', subjectInfo.id)
          .ilike('name', `%${category.replace('-', ' ')}%`)
          .single();

        if (categoryError) {
          console.error('Error fetching category:', categoryError);
        } else {
          setCategoryData(categoryInfo);
          setSelectedCategory(categoryInfo.id);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load test information.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (categoryId: string) => {
    try {
      const { data: topicsData, error: topicsError } = await supabase
        .from('topics')
        .select('id, title')
        .eq('subject_id', subjectData.id)
        .eq('category_id', categoryId)
        .order('title');

      if (topicsError) {
        console.error('Error fetching topics:', topicsError);
        return [];
      }

      return (topicsData || []).map(topic => ({
        id: topic.id,
        name: topic.title
      }));
    } catch (error) {
      console.error('Error fetching topics:', error);
      return [];
    }
  };

  const fetchQuestions = async (subjectId: string, categoryId?: string, topicId?: string) => {
    try {
      let query = supabase
        .from('questions')
        .select(`
          *,
          options (*)
        `)
        .eq('subject_id', subjectId);

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (topicId) {
        query = query.eq('topic_id', topicId);
      }

      const { data: questionsData, error: questionsError } = await query.limit(20);

      if (questionsError) {
        console.error('Error fetching questions:', questionsError);
        toast({
          title: "Error",
          description: "Failed to load questions.",
          variant: "destructive",
        });
        return;
      }

      setQuestions(questionsData || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleModeSelect = (mode: TestMode) => {
    setTestMode(mode);
    setShowModeSelection(false);
  };

  const handleCategorySelect = async (categoryId: string) => {
    setSelectedCategory(categoryId);
    const selectedCat = categories.find(cat => cat.id === categoryId);
    setCategoryData(selectedCat);
    
    const categoryTopics = await fetchTopics(categoryId);
    setTopics(categoryTopics);
    
    if (categoryTopics.length > 0) {
      setShowTopicDialog(true);
    } else {
      await fetchQuestions(subjectData.id, categoryId);
      setTestStarted(true);
    }
  };

  const handleTopicSelect = async (topicId: string) => {
    setSelectedTopic(topicId);
    setShowTopicDialog(false);
    await fetchQuestions(subjectData.id, selectedCategory, topicId);
    setTestStarted(true);
  };

  const handleStartWithoutTopic = async () => {
    setShowTopicDialog(false);
    await fetchQuestions(subjectData.id, selectedCategory);
    setTestStarted(true);
  };

  const handleTestSubmit = async (results: { questionId: string; selectedOptionId: string }[]) => {
    console.log('Test results:', results);

    if (!subjectData) return;

    // Compute score locally for both guests and signed-in users
    let score = 0;
    const answersToInsert = [];
    for (const result of results) {
      const question = questions.find((q) => q.id === result.questionId);
      if (question) {
        const correctOption = question.options.find((opt) => opt.is_correct);
        const isCorrect = result.selectedOptionId === correctOption?.id;
        if (isCorrect) score++;
        answersToInsert.push({
          question_id: result.questionId,
          selected_option_id: result.selectedOptionId || null,
          is_correct: isCorrect,
        });
      }
    }

    const resultsData = {
      questions: questions.map((q) => ({
        id: q.id,
        text: q.text,
        explanation: q.explanation,
        options: q.options,
        correctOptionId: q.options.find((opt) => opt.is_correct)?.id || '',
        categoryName: categoryData?.name,
      })),
      answers: results.reduce((acc, result) => {
        acc[result.questionId] = result.selectedOptionId;
        return acc;
      }, {} as Record<string, string>),
      categories: categories || [],
      grade: grade!,
      subject: subjectData.name,
      categoryName: categoryData?.name,
      score,
      totalQuestions: questions.length,
      isGuest: !user,
    };

    // Guest path: skip DB writes, navigate to inline results view.
    if (!user) {
      toast({
        title: "Test complete!",
        description: `You scored ${score}/${questions.length}. Sign up to save your results.`,
      });
      navigate(`/results/guest`, { state: resultsData });
      return;
    }

    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('test_sessions')
        .insert({
          user_id: user.id,
          subject_id: subjectData.id,
          grade: grade!,
          total_questions: questions.length,
          completed: true,
        })
        .select()
        .single();

      if (sessionError) {
        console.error('Error creating test session:', sessionError);
        toast({
          title: "Error",
          description: "Failed to save test session.",
          variant: "destructive",
        });
        return;
      }

      const { error: answersError } = await supabase
        .from('test_answers')
        .insert(answersToInsert.map((a) => ({ ...a, test_session_id: sessionData.id })));

      if (answersError) {
        console.error('Error saving test answers:', answersError);
      }

      await supabase
        .from('test_sessions')
        .update({ score, time_taken: timeElapsed })
        .eq('id', sessionData.id);

      navigate(`/results/${sessionData.id}`, { state: resultsData });
    } catch (error) {
      console.error('Error submitting test:', error);
      toast({
        title: "Error",
        description: "Failed to submit test. Please try again.",
      });
    }
  };

  const handleBackToModeSelection = () => {
    setTestStarted(false);
    setTestMode(null);
    setShowModeSelection(true);
    setQuestions([]);
    setSelectedCategory(null);
    setCategoryData(null);
    setSelectedTopic(null);
    
    // Update activity context
    updateActivityContext({
      currentActivity: null,
      progress: 0,
      isActive: false
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-xl">Loading test...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subjectData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Subject Not Found</h1>
            <p className="text-gray-600 mb-4">The requested subject could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  if (testStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div>
          <TestInterface
            questions={questions}
            onSubmit={handleTestSubmit}
            subjectName={subjectData.name}
            categoryName={categoryData?.name}
            testMode={testMode}
            onTimeUpdate={setTimeElapsed}
            onBackToSelection={handleBackToModeSelection}
          />
        </div>
      </div>
    );
  }

  if (showModeSelection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto px-4 py-8">
          <TestModeSelection
            onSelectMode={handleModeSelect}
            subjectName={subjectData?.name}
            categoryName={categoryData?.name}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Seo title="Test" description="Take a Grade Test Pro practice test." canonicalPath="/test" noindex />
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold text-gray-800">
              {subjectData?.name} Test - Grade {grade}
            </h1>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-blue-200 mb-6">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-blue-700">
                  Choose a Category
                </CardTitle>
                <p className="text-gray-600">
                  Select which area you'd like to practice
                </p>
              </CardHeader>
              
              <CardContent>
                {categories.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories.map((cat) => (
                      <Card
                        key={cat.id}
                        className="hover:shadow-lg transition-shadow border-2 hover:border-blue-300 flex flex-col"
                      >
                        <CardContent
                          className="p-6 text-center cursor-pointer flex-1"
                          onClick={() => handleCategorySelect(cat.id)}
                        >
                          <BookOpen className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                          <h3 className="font-bold text-lg text-gray-800 mb-2">
                            {cat.name}
                          </h3>
                          {cat.description && (
                            <p className="text-sm text-gray-600">
                              {cat.description}
                            </p>
                          )}
                        </CardContent>
                        <div className="p-4 pt-0 flex flex-col gap-2">
                          <Button
                            size="sm"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleCategorySelect(cat.id)}
                          >
                            Use saved questions
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                            onClick={() =>
                              navigate(
                                `/practice/ai?grade=${grade}&subjectId=${subjectData.id}&categoryId=${cat.id}`,
                              )
                            }
                          >
                            <Sparkles className="h-4 w-4 mr-1" />
                            Generate with AI
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">No categories available for this subject.</p>
                    <Button
                      onClick={() => {
                        fetchQuestions(subjectData.id);
                        setTestStarted(true);
                      }}
                      className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                      disabled={!questions.length}
                    >
                      Start General Test
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>

      <TopicSelectionDialog
        topics={topics}
        open={showTopicDialog}
        onOpenChange={setShowTopicDialog}
        onTopicSelect={handleTopicSelect}
        selectedTopicId={selectedTopic}
        isLoading={false}
        onGoBack={handleStartWithoutTopic}
      />
    </div>
  );
};

export default TestPage;
